import { type FC, useEffect, useRef, useState } from "react";
import { Button, MenuItem, Stack, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useSearchParams } from "react-router-dom";
import {
  useWidgetsQuery,
  useSetDefaultWidgetMutation,
} from "@/partner/entities/widgets";
import { Filter, FilterProvider } from "@/partner/shared/components/Filter";
import { WidgetsList } from "./WidgetsList";
import { CreateWidgetDialog } from "./CreateWidgetDialog";
import { EditWidgetDialog } from "./EditWidgetDialog";

const NO_DEFAULT_WIDGET = "NONE";

/**
 * Страница в общем стиле списков кабинета: карточка Filter с заголовком и
 * кнопкой «Добавить», селект дефолтного виджета в её подшапке, диалоги
 * создания и редактирования открываются по URL (?tab=create,
 * ?tab=edit&id=), как в Products/Promocodes, чтобы на них можно было дать
 * ссылку.
 */
const WidgetsInner: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get("tab");
  const editId = searchParams.get("id");

  const openCreate = () => {
    searchParams.set("tab", "create");
    setSearchParams(searchParams);
  };

  const openEdit = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams);
  };

  const closeDialog = () => {
    searchParams.delete("tab");
    searchParams.delete("id");
    setSearchParams(searchParams);
  };

  // Только для селекта дефолта и поиска редактируемого виджета по id: список
  // организации обычно короткий, первой страницы хватает.
  const { data } = useWidgetsQuery({
    variables: { page: 0, rowsPerPage: 100 },
  });
  const options = data?.widgets.nodes ?? [];
  const editWidget = editId
    ? (options.find((item) => item.id === editId) ?? null)
    : null;

  // Селект отражает реальный дефолт из БД (Widget.isDefault); локальный
  // стейт нужен, чтобы UI не ждал refetch после выбора.
  const currentDefault = options.find((item) => item.isDefault);
  const currentDefaultId = currentDefault?.id ?? NO_DEFAULT_WIDGET;

  const [defaultWidgetId, setDefaultWidgetId] = useState(currentDefaultId);

  useEffect(() => {
    setDefaultWidgetId(currentDefaultId);
  }, [currentDefaultId]);

  // Последний подтверждённый сервером дефолт держим в ref: onError мутации
  // должен откатывать к актуальному значению, а не к захваченному в момент
  // рендера, на котором создан колбэк.
  const currentDefaultIdRef = useRef(currentDefaultId);
  currentDefaultIdRef.current = currentDefaultId;

  const [setDefaultWidget] = useSetDefaultWidgetMutation({
    refetchQueries: ["WidgetsList"],
    onCompleted: () => {
      enqueueSnackbar(t("widgets.defaultWidget.updated"), {
        variant: "success",
      });
    },
    onError: (error) => {
      setDefaultWidgetId(currentDefaultIdRef.current);
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  return (
    <>
      <Stack spacing={1}>
        <Filter
          title={t("widgets.list.title")}
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
            >
              {t("common.actions.add")}
            </Button>
          }
          subheader={
            // Селект дефолта показываем только когда есть хотя бы один
            // виджет: на пустом списке выбирать нечего.
            options.length > 0 ? (
              <TextField
                select
                fullWidth
                variant="outlined"
                label={t("widgets.defaultWidget.label")}
                value={defaultWidgetId}
                onChange={(event) => {
                  const id =
                    event.target.value === NO_DEFAULT_WIDGET
                      ? null
                      : event.target.value;
                  setDefaultWidgetId(event.target.value);
                  void setDefaultWidget({ variables: { id } });
                }}
              >
                <MenuItem value={NO_DEFAULT_WIDGET}>
                  {t("widgets.defaultWidget.none")}
                </MenuItem>
                {options.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.title}
                  </MenuItem>
                ))}
              </TextField>
            ) : undefined
          }
        />

        <WidgetsList onClickRow={(widget) => openEdit(widget.id)} />
      </Stack>

      <CreateWidgetDialog
        open={tab === "create"}
        onClose={closeDialog}
        onCompleted={closeDialog}
      />
      <EditWidgetDialog
        open={tab === "edit" && Boolean(editWidget)}
        widget={editWidget}
        onClose={closeDialog}
        onCompleted={closeDialog}
      />
    </>
  );
};

export const Widgets: FC = () => (
  <FilterProvider>
    <WidgetsInner />
  </FilterProvider>
);
